/**
 * P1.9 — provider coverage at the ENQUEUE chokepoint.
 *
 * `assertAnalysisPrerequisitesReady` consulted materiality and authority
 * readiness — neither knows about provider coverage — so a report whose
 * required providers could not cover the site was ACCEPTED, occupied a queue
 * slot, failed later at dispatch (`applyProviderCoverageDispatchGate` →
 * `ProviderCoverageError`), surfaced as a red Failed row, and was re-created
 * by the invalidation reaper on every SED or intent edit. Same shape as the
 * grid-only defect P1.3 fixed, in a different place.
 *
 * DELIBERATELY NARROWER than the dispatch gate. Dispatch tops the SED up
 * before it gates, so a `missing` section at enqueue may well be collected by
 * the time the job runs, and a `failed` (anomalous) section can be re-fetched
 * by a bootstrap. Refusing those here would manufacture 409s for jobs that
 * would have succeeded. What can NEVER be fixed by the job itself is a
 * required provider that returned no data for this location (`unavailable`)
 * or was skipped as out of coverage (`skipped`) with no successful fallback —
 * those are the jobs this gate refuses. Waivers and coverage overrides are
 * subtracted with the SAME collector the dispatch gate uses, so an admin
 * waiver that lets dispatch proceed lets enqueue proceed.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteAuditOverride } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { allowsScreeningWithSourceGaps } from '../reportGeneration/screeningPolicy';
import {
  checkProviderCoverage,
  collectWaivedProviderIds,
  type ProviderCoverageItem,
  readEvidenceSection,
} from './providerCoverageGate';
import { describeUnavailableSection, type UnavailableSectionFields } from '../evidenceUnavailableReason';

/** Outcomes no dispatch-time top-up or bootstrap retry changes. */
const PERMANENT_STATUSES: ReadonlySet<ProviderCoverageItem['status']> = new Set(['unavailable', 'skipped']);

export interface EnqueueCoverageRefusal {
  items: ProviderCoverageItem[];
  message: string;
}

export function enqueueCoverageRefusalFor(
  reportTypeId: ReportTypeId,
  sed: SiteEvidenceDocument | null | undefined,
  opts: {
    dispatchWaivers?: { providerId: string }[];
    auditOverrides?: SiteAuditOverride[];
  } = {},
): EnqueueCoverageRefusal | null {
  // Location screening and refinements retain gaps in their evidence receipt;
  // utility/provider completeness is not a prerequisite for describing a site.
  if (allowsScreeningWithSourceGaps(reportTypeId)) return null;
  // No SED yet ⇒ nothing to judge; the generation path bootstraps first.
  if (!sed) return null;
  // `bootstrapMode` keeps `missing` non-blocking, matching the dispatch top-up.
  const report = checkProviderCoverage(reportTypeId, sed, { bootstrapMode: true });
  const waived = collectWaivedProviderIds({ ...opts, reportTypeId });
  const items = report.missingRequired.filter(
    (item) => PERMANENT_STATUSES.has(item.status) && !waived.has(item.providerId),
  );
  if (items.length === 0) return null;
  const labels = items.map((item) => `${item.providerLabel} (${item.status})`).join(', ');
  // The closing sentence asked the user to work out for themselves whether the
  // outage was transient — from a status that cannot distinguish a 429 from a
  // provider that does not cover this location. Both refuse the job here, but
  // one is fixed by re-running and the other only by a waiver, and guessing
  // wrong either wastes a rebuild or waives a gate over a transient blip. Name
  // the cause per provider from what the producer stamped.
  //
  // WHICH ITEMS ARE REFUSED IS UNCHANGED: `PERMANENT_STATUSES` still decides,
  // so nothing that was refused now enqueues. The section 7 audit proposed
  // narrowing permanence to the truly permanent causes; that would let a
  // transiently-failed provider enqueue a job the dispatch gate then throws on,
  // re-created on every evidence change — the loop this refusal exists to stop.
  const causes = items
    .map((item) => {
      const section = item.sedSectionId ? readEvidenceSection(sed, item.sedSectionId) : null;
      const { kind, retryable, reason } = describeUnavailableSection(section as UnavailableSectionFields | null);
      const because = reason ? ` (${reason})` : '';
      if (kind === 'needs-admin-config') {
        // auth-missing / auth-invalid / misconfigured all reach the section as
        // attemptResult 'http-error', so a retry prescription here would loop.
        return `${item.providerLabel}: not configured for this deployment${because} — an admin must fix the credential or integration; re-running fails the same way.`;
      }
      if (item.status === 'skipped' || kind === 'not-attempted') {
        return `${item.providerLabel}: never called${because} — an admin must enable it, then re-run the SED bootstrap.`;
      }
      if (kind === 'out-of-coverage') {
        return `${item.providerLabel}: does not cover this location${because} — this needs an admin coverage waiver, not a re-run.`;
      }
      if (retryable) {
        return `${item.providerLabel}: the provider failed on the last attempt${because} — re-run the SED bootstrap to retry it.`;
      }
      return `${item.providerLabel}: queried, no record for this site${because} — supply it as a document or request an admin waiver.`;
    })
    .join(' ');
  return {
    items,
    message:
      `Required evidence cannot be collected for this site: ${labels}. `
      + 'The job is not queued because it would fail at dispatch and be re-created on every '
      + `evidence change. ${causes}`,
  };
}
